import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import UserPaymentUpload from './UserPaymentUpload';
import { OcularVisitStep } from './OcularVisitStep';

export default function UserDashboardStatus({ process, financialDetails, onUpdate, toast, status, message }) {
    const [activeStep, setActiveStep] = useState(null);
    if (status === 'waiting_for_process') {
        return (
            <div className="flex flex-column gap-3">
                <Card className="shadow-1 border-none text-center p-5">
                    <div className="mb-4">
                        <i className="pi pi-clock text-6xl text-blue-500"></i>
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-900">Estás en espera</h2>
                    <p className="text-gray-600 text-lg mb-4 line-height-3">
                        {message || "Tu proceso aún no ha comenzado. Por favor espera a que un administrador inicie tu trámite."}
                    </p>
                    <div className="surface-ground p-4 border-round-xl inline-block max-w-30rem">
                        <p className="text-sm text-gray-600 m-0">
                            <strong>Relájate, no tienes que hacer nada más por ahora.</strong><br />
                            Te enviaremos un correo de confirmación cuando inicie tu proceso o puedes revisar este dashboard más tarde.
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // Sync active step with process current_step if not set
    const steps = [
        { id: 0, label: 'Visita Ocular', step_type: 'ocular_visit', icon: 'pi pi-eye' },
        { id: 1, label: 'Cotización', step_type: 'confirm_quote', icon: 'pi pi-file' },
        { id: 2, label: 'Pago Inicial', step_type: 'upload_payment_proof', icon: 'pi pi-upload' },
        { id: 3, label: 'Facturación', step_type: 'request_invoice', icon: 'pi pi-receipt' },
    ];

    // Determine the truly active step based on status
    const getActiveStepIndex = () => {
        if (!process?.steps) return 0;

        // Find the first step that is NOT completed (in-progress, pending, error)
        const firstIncompleteStepIndex = steps.findIndex(step => {
            const processStep = process.steps.find(s => s.step_type === step.step_type);
            // If step exists and is completed, it's NOT the active one. 
            // If it doesn't exist (pending) or is in-progress/error, it IS the active one.
            return !processStep || processStep.status !== 'completed';
        });

        // If all found steps are completed, create index for next step if available, else last step
        if (firstIncompleteStepIndex === -1) {
            // Check if we actually have all steps completed in process.steps
            const lastStepType = steps[steps.length - 1].step_type;
            const isLastCompleted = process.steps.some(s => s.step_type === lastStepType && s.status === 'completed');
            return isLastCompleted ? steps.length - 1 : 0;
        }

        return firstIncompleteStepIndex;
    };

    const backendCurrentStepIndex = process?.steps?.findIndex(s => s.step_type === process.current_step);
    // Prefer the calculated first incomplete step, but fallback to backend pointer if reasonable
    const calculatedStepIndex = getActiveStepIndex();

    // If backend points to a completed step, force move to next (calculated)
    const effectiveActiveStep = activeStep !== null ? activeStep : calculatedStepIndex;
    const currentStepIndex = calculatedStepIndex; // Used for locking future steps

    const getStepState = (stepType) => {
        const step = process?.steps?.find(s => s.step_type === stepType);
        return step || { status: 'pending' };
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return { icon: 'pi pi-check-circle', color: '#16a34a' };
            case 'in-progress': return { icon: 'pi pi-clock', color: '#f59e0b' };
            case 'error': return { icon: 'pi pi-exclamation-triangle', color: '#dc2626' };
            case 'unsuccessful': return { icon: 'pi pi-times-circle', color: '#dc2626' };
            default: return { icon: 'pi pi-circle', color: '#9ca3af' };
        }
    };

    const renderStepContent = () => {
        const selectedStep = steps[effectiveActiveStep];
        const stepData = getStepState(selectedStep.step_type);
        switch (selectedStep.step_type) {
            case 'ocular_visit':
                return <OcularVisitStep step={stepData} />;
            case 'confirm_quote':
                if (stepData.status === 'completed') {
                    return (
                        <div className="p-4 text-center">
                            <i className="pi pi-check-circle text-6xl text-green-500 mb-3"></i>
                            <h3 className="text-xl font-bold mb-2">Cotización Confirmada</h3>
                            <p className="text-gray-600">Tu cotización ha sido aprobada y validada por nuestro equipo.</p>
                            <div className="bg-gray-50 p-3 border-round mt-4 inline-block">
                                <span className="text-sm font-semibold text-gray-500 uppercase block">MENSAJE DEL ASESOR</span>
                                <span className="italic font-medium">"Tu cotización ha sido aprobada. Puedes proceder con el pago inicial."</span>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="p-4 text-center">
                        <i className="pi pi-clock text-6xl text-orange-500 mb-3"></i>
                        <h3 className="text-xl font-bold mb-2">Evaluando Cotización</h3>
                        <p className="text-gray-600">Estamos analizando los resultados de la visita para preparar tu cotización final.</p>
                        <div className="bg-orange-50 p-3 border-round mt-4 inline-block">
                            <span className="text-sm font-semibold text-orange-600 uppercase block">ESTADO ACTUAL</span>
                            <span className="italic font-medium text-orange-800">"El administrador está evaluando tu cotización"</span>
                        </div>
                    </div>
                );
            case 'upload_payment_proof':
                return (
                    <UserPaymentUpload
                        step={stepData}
                        financialDetails={financialDetails}
                        onUpdate={onUpdate}
                        toast={toast}
                    />
                );
            case 'request_invoice':
                return (
                    <div className="p-4 text-center">
                        <i className="pi pi-receipt text-6xl text-blue-300 mb-3"></i>
                        <h3 className="text-xl font-bold mb-2">Facturación</h3>Este paso esta en nuestras manos, tu relajate, te haremos llegar cualquier actualización.
                        <p className="text-gray-600"></p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-column gap-3">
            <Card className="shadow-1 border-none">
                <div className="flex flex-column mb-4">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-lg font-bold">Progreso de mi Solicitud</span>
                        <span className="text-blue-600 font-bold">{process?.progress || 0}%</span>
                    </div>
                    <ProgressBar value={process?.progress || 0} showValue={false} style={{ height: '8px' }} />
                </div>

                <div className="flex justify-content-between align-items-center gap-2 overflow-x-auto pb-2">
                    {steps.map((step, index) => {
                        const stepData = getStepState(step.step_type);
                        const { icon, color } = getStatusIcon(stepData.status);
                        const isActive = effectiveActiveStep === index;
                        const isLocked = index > (currentStepIndex === -1 ? 0 : currentStepIndex);

                        return (
                            <React.Fragment key={step.id}>
                                <div
                                    className={`flex align-items-center gap-2 p-2 border-round-lg cursor-pointer transition-colors ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                    onClick={() => !isLocked && setActiveStep(index)}
                                    style={{ opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                >
                                    <i className={icon} style={{ color, fontSize: '1.2rem' }}></i>
                                    <span className={`text-sm font-semibold whitespace-nowrap ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                                        {step.label}
                                    </span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className="h-px bg-gray-200 flex-grow" style={{ minWidth: '20px' }}></div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </Card>

            <Card className="shadow-1 border-none render-content">
                {renderStepContent()}
            </Card>
        </div>
    );
}
